import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FullscreenError from '@/components/utils/FullscreenError';
import Badge from '@/components/ui/Badge';
import { faArrowLeft, faCalendarAlt, faIdCard, faSpinner, faTrash, faUser } from '@/fontawesome';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { UserQuestion, UserQuestionID } from '@/models';
import type { UpdateUserQuestionRequest } from '@/modelsAPI';
import { USER_QUESTION_PRIORITIES, clampPriority, getUserQuestionStatusColor, getUserQuestionStatusLabel } from '@/modelsLocal';

export default function AdminUserQuestionDetail() {
  const { questionId } = useParams<{ questionId: string }>();
  const navigate = useNavigate();
  const [question, setQuestion] = useState<UserQuestion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (questionId !== undefined) {
      fetchQuestion();
    }
  }, [questionId]);

  const fetchQuestion = async () => {
    if (questionId === undefined) return;

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);
      const response = await APIRoutes.getUserQuestion(questionId as UserQuestionID);
      setQuestion(response);
    } catch (err) {
      console.error('Critical error loading user question:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Frage');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const updateQuestion = async (request: UpdateUserQuestionRequest, successMessage: string) => {
    if (question === null) return;
    try {
      setSaving(true);
      const updated = await APIRoutes.updateUserQuestion(question.id, request);
      // Die PATCH-Antwort enthält den Lookup-User nicht zwingend
      setQuestion({ ...updated, user: updated.user ?? question.user });
      showSuccessToast(successMessage);
    } catch (err) {
      handleAPIError(err, 'Frage konnte nicht aktualisiert werden.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (question === null || !confirm('Frage wirklich löschen?')) return;
    try {
      setSaving(true);
      await APIRoutes.deleteUserQuestion(question.id);
      showSuccessToast('Frage gelöscht');
      navigate('/admin/questions');
    } catch (err) {
      handleAPIError(err, 'Frage konnte nicht gelöscht werden.');
      setSaving(false);
    }
  };

  if (criticalError) {
    return (
      <FullscreenError
        title="Frage nicht verfügbar"
        message={error ?? 'Die Frage konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchQuestion}
        onBack={() => navigate('/admin/questions')}
      />
    );
  }

  const isDone = question?.status === 'DONE';
  const priority = question !== null ? USER_QUESTION_PRIORITIES[clampPriority(question.priority)] : null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center mb-8">
        <Link to="/admin/questions" className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
        </Link>
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white break-words">
            {question?.title ?? 'Frage'}
          </h1>
          {question !== null && (
            <Badge color={getUserQuestionStatusColor(question.status)} className="mt-2">
              {getUserQuestionStatusLabel(question.status)}
            </Badge>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      )}

      {question !== null && !loading && priority !== null && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Frage</h2>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">{question.body}</p>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FontAwesomeIcon icon={faUser} className="w-4 h-4 mr-1" />
                  Benutzer
                </label>
                <Link
                  to={`/admin/users/${question.userID}`}
                  className="block text-sm text-blue-600 dark:text-blue-400 hover:underline bg-gray-50 dark:bg-gray-700 p-2 rounded"
                >
                  {question.user !== null ? (
                    <>
                      <div className="font-medium">{question.user.firstName} {question.user.lastName}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{question.user.email}</div>
                    </>
                  ) : (
                    <span className="font-mono">{question.userID}</span>
                  )}
                </Link>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FontAwesomeIcon icon={faIdCard} className="w-4 h-4 mr-1" />
                  Fragen-ID
                </label>
                <div className="text-sm text-gray-900 dark:text-white font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded">
                  {question.id}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                  Erstellt
                </label>
                <div className="text-sm text-gray-900 dark:text-white">{formatDate(question.created)}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                  Erledigt am
                </label>
                <div className="text-sm text-gray-900 dark:text-white">
                  {question.completedAt !== null ? formatDate(question.completedAt) : '–'}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Bearbeiten</h2>
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <label htmlFor="question-priority" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Priorität
                </label>
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${priority.dot}`} />
                  <select
                    id="question-priority"
                    value={priority.value}
                    disabled={saving}
                    onChange={(e) => updateQuestion({ priority: Number(e.target.value) }, 'Priorität gespeichert')}
                    className="text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {USER_QUESTION_PRIORITIES.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="button"
                disabled={saving}
                onClick={() => updateQuestion({ status: isDone ? 'OPEN' : 'DONE' }, isDone ? 'Frage wieder geöffnet' : 'Frage als erledigt markiert')}
                className={`px-4 py-2 rounded-md text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  isDone ? 'bg-gray-600 hover:bg-gray-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isDone ? 'Wieder öffnen' : 'Als erledigt markieren'}
              </button>

              <button
                type="button"
                disabled={saving}
                onClick={handleDelete}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors ml-auto"
              >
                <FontAwesomeIcon icon={saving ? faSpinner : faTrash} className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
